import React from 'react';
import styled from 'styled-components';
import { theme } from '../../styles/theme';

interface SleepData {
  date: string;
  start_time: string;
  end_time: string;
  quality: string;
  notes: string;
}

interface WeeklyAverage {
  week: string;
  averageHours: number;
  weekStart: string;
  weekEnd: string;
}

interface SleepChartProps {
  sleepData: SleepData[];
}

const ChartContainer = styled.div`
  position: relative;
  width: 100%;
  height: 400px;
  padding: ${theme.spacing.lg};
  background: ${theme.colors.white};
  border-radius: ${theme.borderRadius.lg};
  border: 1px solid ${theme.colors.gray[200]};
`;

const ChartTitle = styled.h3`
  margin: 0 0 ${theme.spacing.lg} 0;
  color: ${theme.colors.primary};
  font-size: ${theme.typography.sizes.lg};
  font-weight: ${theme.typography.weights.semibold};
  text-align: center;
`;

const ChartSvg = styled.svg`
  width: 100%;
  height: 250px;
  overflow: visible;
  border: 1px solid ${theme.colors.gray[100]};
`;

const GridLine = styled.line`
  stroke: ${theme.colors.gray[300]};
  stroke-width: 1.5;
  stroke-dasharray: 3,3;
  opacity: 0.8;
`;

const AxisLabel = styled.text`
  fill: ${theme.colors.gray[700]};
  font-size: 14px;
  font-weight: 500;
  font-family: ${theme.typography.fontFamily};
`;



const ChartLine = styled.path`
  fill: none;
  stroke: ${theme.colors.primary};
  stroke-width: 2;
  stroke-linecap: round;
  stroke-linejoin: round;
  stroke-dasharray: 5,5;
  opacity: 0.5;
`;

const DataPoint = styled.circle`
  fill: ${theme.colors.primary};
  stroke: ${theme.colors.white};
  stroke-width: 3;
  cursor: pointer;
  transition: all 0.2s ease;
  
  &:hover {
    fill: ${theme.colors.accent};
    r: 6;
  }
`;

const Tooltip = styled.div<{ x: number; y: number; visible: boolean }>`
  position: absolute;
  left: ${({ x }) => x}px;
  top: ${({ y }) => y}px;
  background: ${theme.colors.gray[900]};
  color: ${theme.colors.white};
  padding: ${theme.spacing.sm};
  border-radius: ${theme.borderRadius.md};
  font-size: ${theme.typography.sizes.sm};
  pointer-events: none;
  opacity: ${({ visible }) => visible ? 1 : 0};
  transform: translate(-50%, -100%);
  transition: opacity 0.2s ease;
  z-index: 10;
  
  &::after {
    content: '';
    position: absolute;
    top: 100%;
    left: 50%;
    border: 5px solid transparent;
    border-top-color: ${theme.colors.gray[900]};
    transform: translateX(-50%);
  }
`;

const Legend = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  gap: ${theme.spacing.md};
  margin-top: ${theme.spacing.md};
  font-size: ${theme.typography.sizes.sm};
  color: ${theme.colors.gray[600]};
`;

const LegendItem = styled.div`
  display: flex;
  align-items: center;
  gap: ${theme.spacing.xs};
`;

const LegendDot = styled.div`
  width: 12px;
  height: 12px;
  border-radius: 50%;
  background: ${theme.colors.primary};
`;

const LegendLine = styled.div`
  width: 20px;
  height: 3px;
  background: ${theme.colors.primary};
  border-radius: 2px;
`;

export const SleepChart: React.FC<SleepChartProps> = ({ sleepData }) => {
  const [tooltip, setTooltip] = React.useState<{
    visible: boolean;
    x: number;
    y: number;
    content: string;
  }>({ visible: false, x: 0, y: 0, content: '' });

  // Calculate sleep hours
  const calculateSleepHours = (start: string, end: string): number => {
    try {
      console.log(`Calculating sleep hours for: ${start} to ${end}`);
      
      const startTime = new Date(`2024-01-01 ${start}`);
      let endTime = new Date(`2024-01-01 ${end}`);
      
      // Check for invalid dates
      if (isNaN(startTime.getTime()) || isNaN(endTime.getTime())) {
        console.error(`Invalid time format: ${start} to ${end}`);
        return 0;
      }
      
      // Handle midnight crossover
      if (start > end || start.startsWith('24:')) {
        endTime = new Date(`2024-01-02 ${end}`);
      }
      if (start.startsWith('24:')) {
        startTime.setDate(startTime.getDate() + 1);
        startTime.setHours(0, parseInt(start.split(':')[1]));
      }
      
      const diffMs = endTime.getTime() - startTime.getTime();
      const hours = Math.round((diffMs / (1000 * 60 * 60)) * 10) / 10;
      
      console.log(`Sleep calculation result: ${hours} hours`);
      return hours;
    } catch (error) {
      console.error(`Error calculating sleep hours for ${start} to ${end}:`, error);
      return 0;
    }
  };

  // Group data by weeks and calculate averages
  const getWeeklyAverages = (): WeeklyAverage[] => {
    const weeks: { [key: string]: SleepData[] } = {};
    
    sleepData.forEach(sleep => {
      const date = new Date(sleep.date);
      const mondayOfWeek = new Date(date);
      mondayOfWeek.setDate(date.getDate() - ((date.getDay() + 6) % 7));
      
      const weekKey = mondayOfWeek.toISOString().split('T')[0];
      if (!weeks[weekKey]) {
        weeks[weekKey] = [];
      }
      weeks[weekKey].push(sleep);
    });

    return Object.entries(weeks)
      .map(([weekStart, weekData]) => {
        const totalHours = weekData.reduce((sum, sleep) => {
          return sum + calculateSleepHours(sleep.start_time, sleep.end_time);
        }, 0);
        
        const averageHours = totalHours / weekData.length;
        const endDate = new Date(weekStart);
        endDate.setDate(endDate.getDate() + 6);
        
        return {
          week: `Week of ${new Date(weekStart).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}`,
          averageHours: Math.round(averageHours * 10) / 10,
          weekStart,
          weekEnd: endDate.toISOString().split('T')[0]
        };
      })
      .sort((a, b) => new Date(a.weekStart).getTime() - new Date(b.weekStart).getTime())
      .slice(-4); // Last 4 weeks
  };

  const weeklyData = getWeeklyAverages();
  
  // Chart dimensions
  const chartWidth = 650;
  const chartHeight = 250;
  const padding = 30;

  // Ensure we have data
  if (weeklyData.length === 0) {
    return (
      <ChartContainer>
        <ChartTitle>Average Sleep Hours by Week</ChartTitle>
        <div style={{ textAlign: 'center', color: '#666', marginTop: '50px' }}>
          No sleep data available
        </div>
      </ChartContainer>
    );
  }

  // Calculate scales with better bounds
  const allHours = weeklyData.map(d => d.averageHours).filter(h => !isNaN(h) && h > 0);
  
  if (allHours.length === 0) {
    return (
      <ChartContainer>
        <ChartTitle>Average Sleep Hours by Week</ChartTitle>
        <div style={{ textAlign: 'center', color: '#666', marginTop: '50px' }}>
          No valid sleep data available
        </div>
      </ChartContainer>
    );
  }
  
  const minHours = Math.max(0, Math.min(...allHours) - 0.5);
  const maxHours = Math.max(...allHours) + 0.5;
  const hourRange = maxHours - minHours;
  
  const xScale = (index: number) => {
    if (weeklyData.length === 1) return chartWidth / 2;
    return padding + (index * (chartWidth - 2 * padding)) / Math.max(1, weeklyData.length - 1);
  };
  
  const yScale = (hours: number) => {
    return chartHeight - padding - ((hours - minHours) / hourRange) * (chartHeight - 2 * padding);
  };

  // Generate path for dotted line connecting data points
  const linePath = weeklyData
    .map((d, i) => `${i === 0 ? 'M' : 'L'} ${xScale(i)} ${yScale(d.averageHours)}`)
    .join(' ');

  // Generate horizontal grid lines with whole numbers only
  const yGridLines: React.ReactElement[] = [];
  const yLabels: React.ReactElement[] = [];
  
  // Calculate grid values (whole numbers only)
  const gridValues: number[] = [];
  const startValue = Math.floor(minHours); // Round down to nearest whole number
  const endValue = Math.ceil(maxHours);     // Round up to nearest whole number
  
  for (let value = startValue; value <= endValue; value++) {
    if (value >= minHours && value <= maxHours) {
      gridValues.push(value);
    }
  }
  
  // Generate grid lines and labels
  gridValues.forEach((hours, i) => {
    const yPos = yScale(hours);
    yGridLines.push(
      <GridLine
        key={`y-grid-${i}`}
        x1={padding}
        y1={yPos}
        x2={chartWidth - padding}
        y2={yPos}
      />
    );
    
    yLabels.push(
      <AxisLabel
        key={`y-label-${i}`}
        x={padding - 15}
        y={yPos + 5}
        textAnchor="end"
        dominantBaseline="middle"
      >
        {hours}h
      </AxisLabel>
    );
  });

  const handleMouseEnter = (event: React.MouseEvent, data: WeeklyAverage, index: number) => {
    const rect = (event.currentTarget as SVGElement).getBoundingClientRect();
    const containerRect = (event.currentTarget as SVGElement).closest('.chart-container')?.getBoundingClientRect();
    
    if (containerRect) {
      setTooltip({
        visible: true,
        x: rect.left + rect.width / 2 - containerRect.left,
        y: rect.top - containerRect.top - 10,
        content: `${data.week}: ${data.averageHours} hours avg`
      });
    }
  };

  const handleMouseLeave = () => {
    setTooltip(prev => ({ ...prev, visible: false }));
  };

  return (
    <ChartContainer className="chart-container">
      <ChartTitle>Average Sleep Hours by Week</ChartTitle>
      
      <ChartSvg viewBox={`0 0 ${chartWidth} ${chartHeight}`}>
        {/* Grid lines */}
        {yGridLines}
        
        {/* Dotted line connecting data points */}
        <ChartLine d={linePath} />
        
        {/* Data points */}
        {weeklyData.map((data, index) => {
          const x = xScale(index);
          const y = yScale(data.averageHours);
          return (
            <DataPoint
              key={`point-${index}`}
              cx={x}
              cy={y}
              r={5}
              onMouseEnter={(e) => handleMouseEnter(e, data, index)}
              onMouseLeave={handleMouseLeave}
            />
          );
        })}
        
        {/* Y-axis labels */}
        {yLabels}
        
        {/* X-axis labels */}
        {weeklyData.map((data, index) => (
          <AxisLabel
            key={`x-label-${index}`}
            x={xScale(index)}
            y={chartHeight - 10}
            textAnchor="middle"
          >
            {data.week.split(' ').slice(-2).join(' ')}
          </AxisLabel>
        ))}
      </ChartSvg>
      
      <Tooltip
        x={tooltip.x}
        y={tooltip.y}
        visible={tooltip.visible}
      >
        {tooltip.content}
      </Tooltip>
      
      <Legend>
        <LegendItem>
          <LegendDot />
          <span>Weekly Average</span>
        </LegendItem>
        <LegendItem>
          <LegendLine />
          <span>Trend Line</span>
        </LegendItem>
      </Legend>
    </ChartContainer>
  );
};